import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    BookOpen, Package, FileText, Image as ImageIcon, Zap, Tag,
    ChevronRight, ChevronDown, Eye, CheckCircle2, AlertCircle,
    Star, ShoppingCart, ArrowRight, Info, Lightbulb, Heart, IndianRupee,
    BadgePercent, MapPin, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';

const VendorProductGuide = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const userRole = user?.role === 'admin' ? 'admin' : 'vendor';
    const [expandedSection, setExpandedSection] = useState('basic');
    const [showPreview, setShowPreview] = useState(true);

    const sections = [
        {
            id: 'basic',
            title: 'Step 1: Basic Information',
            icon: Package,
            color: 'bg-blue-500',
            description: 'The essential details that identify your product',
            fields: [
                {
                    name: 'Product Name',
                    example: 'Tata Power Solar 3kW On-Grid System',
                    required: true,
                    tip: 'Use the full official product name with brand, size, and type. This appears as the main title on the product page.',
                    mapsTo: 'Product title on the listing page'
                },
                {
                    name: 'Brand',
                    example: 'Tata Power Solar',
                    required: true,
                    tip: 'The manufacturer brand name. Displayed under the product title and used for filtering.',
                    mapsTo: 'Brand badge & filter'
                },
                {
                    name: 'Category',
                    example: 'Home / Commercial',
                    required: true,
                    tip: 'Choose "Home" for residential systems or "Commercial" for business/industrial.',
                    mapsTo: 'Category tag & shop filters'
                },
                {
                    name: 'System Size (kW)',
                    example: '3',
                    required: true,
                    tip: 'The power output in kilowatts. This is used to calculate subsidy amounts under PM Surya Ghar scheme.',
                    mapsTo: 'System size badge & subsidy calculation'
                },
                {
                    name: 'Suggested Price (₹)',
                    example: '185000',
                    required: true,
                    tip: 'Your suggested selling price. Admin may adjust this. This is the price customers will see.',
                    mapsTo: 'Product price on listing & detail pages'
                },
                {
                    name: 'Warranty (Years)',
                    example: '25',
                    required: true,
                    tip: 'Product warranty period. Longer warranties build customer trust.',
                    mapsTo: 'Warranty badge on product page'
                },
                {
                    name: 'Subsidy Eligible',
                    example: 'Yes / No toggle',
                    required: false,
                    tip: 'Enable if this product qualifies for PM Surya Ghar government subsidy. The subsidy amount is auto-calculated.',
                    mapsTo: 'Green "Subsidy" badge + price-after-subsidy display'
                }
            ]
        },
        {
            id: 'description',
            title: 'Step 2: Description',
            icon: FileText,
            color: 'bg-purple-500',
            description: 'Tell customers about your product in detail',
            fields: [
                {
                    name: 'Short Description',
                    example: 'High-efficiency monocrystalline solar panel system with 25-year performance warranty.',
                    required: true,
                    tip: 'One-line summary (50-100 words). This appears on the product card in search results and shop page.',
                    mapsTo: 'Product card subtitle in shop grid'
                },
                {
                    name: 'Long Description',
                    example: 'The Tata Power Solar 3kW system features premium monocrystalline cells with 21.5% efficiency. Designed for Indian rooftops, this system generates approximately 12-15 units of electricity daily, reducing your electricity bill by ₹2,000-3,000 per month...',
                    required: true,
                    tip: 'Detailed product description (200-500 words). Include: what it does, who it\'s for, key benefits, technical highlights. This is the main "Description" tab on the product detail page.',
                    mapsTo: 'Description tab on product detail page'
                }
            ]
        },
        {
            id: 'images',
            title: 'Step 3: Product Images',
            icon: ImageIcon,
            color: 'bg-green-500',
            description: 'Upload clear, high-quality product photos',
            fields: [
                {
                    name: 'Main Product Image',
                    example: 'Front-facing clear photo of the panel/system',
                    required: true,
                    tip: 'The hero image shown on the product card and as the main photo on the product page. Use a clean, well-lit photo with white/neutral background. Recommended: 800×800px or larger.',
                    mapsTo: 'Product card image + main product photo'
                },
                {
                    name: 'Gallery Images (up to 3)',
                    example: 'Side view, installed photo, close-up of components',
                    required: false,
                    tip: 'Additional images shown as thumbnails below the main image on the product detail page. Show different angles, close-ups, and installation photos.',
                    mapsTo: 'Image gallery thumbnails on product page'
                }
            ]
        },
        {
            id: 'features',
            title: 'Step 4: Features & Specifications',
            icon: Zap,
            color: 'bg-orange-500',
            description: 'Technical details that help customers compare products',
            fields: [
                {
                    name: 'Key Features (up to 4)',
                    example: [
                        '21.5% Module Efficiency',
                        'IP68 Dust & Water Resistant',
                        'Anti-PID Technology',
                        '25-Year Performance Warranty'
                    ],
                    required: true,
                    tip: 'Short bullet points highlighting the product\'s best selling points. These appear as checkmarks (✓) on the product detail page. Keep each under 8 words.',
                    mapsTo: 'Feature bullets with checkmarks on product page'
                },
                {
                    name: 'Specifications',
                    example: [
                        { label: 'Wattage', value: '400W' },
                        { label: 'Efficiency', value: '21.5%' },
                        { label: 'Dimensions', value: '1755×1038×35mm' },
                        { label: 'Weight', value: '21.5 kg' }
                    ],
                    required: true,
                    tip: 'Technical specs table. Each has a Label (e.g., "Wattage") and Value (e.g., "400W"). These appear in the "Specifications" tab on the product page.',
                    mapsTo: 'Specifications tab on product detail page'
                }
            ]
        }
    ];

    return (
        <DashboardLayout userRole={userRole}>
            <div className="space-y-6 max-w-5xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                            <BookOpen className="w-8 h-8 text-primary" />
                            Product Listing Guide
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Learn what information to provide when suggesting a new product for the catalog
                        </p>
                    </div>
                    <Button onClick={() => navigate(userRole === 'admin' ? '/admin/products' : '/vendor/products')} className="btn-primary">
                        <Package className="w-4 h-4 mr-2" /> Go to Products
                    </Button>
                </div>

                {/* How It Works Banner */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl bg-gradient-to-r from-primary/10 via-blue-500/10 to-purple-500/10 border border-primary/20 p-6"
                >
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-yellow-500" />
                        How Product Listing Works
                    </h2>
                    <div className="grid md:grid-cols-4 gap-4">
                        {[
                            { step: 1, title: 'You Suggest', desc: 'Fill in product details in the "Suggest Product" form', icon: FileText, color: 'text-blue-500' },
                            { step: 2, title: 'Admin Reviews', desc: 'Admin checks quality, sets final price, and approves', icon: Eye, color: 'text-purple-500' },
                            { step: 3, title: 'Goes Live', desc: 'Product appears on the website shop page for customers', icon: CheckCircle2, color: 'text-green-500' },
                            { step: 4, title: 'Customers Buy', desc: 'Orders come to your vendor dashboard', icon: ShoppingCart, color: 'text-orange-500' }
                        ].map(item => (
                            <div key={item.step} className="flex items-start gap-3">
                                <div className={`w-10 h-10 rounded-xl bg-background border flex items-center justify-center flex-shrink-0 ${item.color}`}>
                                    <item.icon className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground">Step {item.step}</div>
                                    <div className="font-semibold text-sm">{item.title}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">{item.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Live Product Preview */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="rounded-2xl border bg-card overflow-hidden"
                >
                    <button
                        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                        onClick={() => setShowPreview(!showPreview)}
                    >
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <Eye className="w-5 h-5 text-primary" />
                            Product Page Preview — Where Each Field Appears
                        </h2>
                        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showPreview ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                        {showPreview && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="px-4 pb-6">
                                    {/* Mock Product Card */}
                                    <div className="mb-6">
                                        <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                                            📋 Shop Page — Product Card
                                        </h3>
                                        <div className="max-w-sm border rounded-2xl overflow-hidden bg-background shadow-sm">
                                            {/* Mock Image */}
                                            <div className="aspect-square bg-gradient-to-br from-blue-50 to-blue-100 relative flex items-center justify-center">
                                                <div className="text-center p-4">
                                                    <ImageIcon className="w-12 h-12 text-blue-300 mx-auto mb-2" />
                                                    <span className="text-xs text-blue-400 font-medium">Main Product Image</span>
                                                </div>
                                                <div className="absolute top-3 left-3 flex flex-col gap-1">
                                                    <Badge className="bg-green-500 text-white text-[10px]"><BadgePercent className="w-3 h-3 mr-1" /> Subsidy</Badge>
                                                </div>
                                                {/* Annotations */}
                                                <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 text-[10px] px-2 py-0.5 rounded-full font-bold animate-pulse">
                                                    ← Main Image
                                                </div>
                                                <div className="absolute bottom-3 left-3 bg-yellow-400 text-yellow-900 text-[10px] px-2 py-0.5 rounded-full font-bold">
                                                    ← Subsidy Badge
                                                </div>
                                            </div>
                                            <div className="p-4 space-y-2">
                                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                                    <span className="uppercase tracking-wide relative">
                                                        home
                                                        <span className="absolute -top-5 left-0 bg-yellow-400 text-yellow-900 text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap">↓ Category</span>
                                                    </span>
                                                    <span className="relative">
                                                        Tata Power Solar
                                                        <span className="absolute -top-5 right-0 bg-yellow-400 text-yellow-900 text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap">Brand ↓</span>
                                                    </span>
                                                </div>
                                                <h3 className="font-semibold text-foreground relative">
                                                    Tata Power Solar 3kW On-Grid System
                                                    <span className="absolute -right-2 top-0 bg-yellow-400 text-yellow-900 text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap">← Product Name</span>
                                                </h3>
                                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                    <Zap className="w-4 h-4 text-primary" />
                                                    <span className="relative">
                                                        3 kW System
                                                        <span className="absolute -top-5 left-0 bg-yellow-400 text-yellow-900 text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap">↓ System Size</span>
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {[1, 2, 3, 4, 5].map(i => (
                                                        <Star key={i} className={`w-3 h-3 ${i <= 4 ? 'fill-primary text-primary' : 'text-muted'}`} />
                                                    ))}
                                                    <span className="text-xs text-muted-foreground">(0)</span>
                                                </div>
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2 relative">
                                                        <span className="text-lg font-bold text-primary">₹1,85,000</span>
                                                        <span className="absolute -right-2 -top-1 bg-yellow-400 text-yellow-900 text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap">← Price</span>
                                                    </div>
                                                    <div className="text-xs text-green-600 font-medium relative">
                                                        After Subsidy: ₹1,07,000
                                                        <span className="absolute right-0 -top-1 bg-yellow-400 text-yellow-900 text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap">← Auto-calculated</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Mock Product Detail Page */}
                                    <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                                        📄 Product Detail Page — Full View
                                    </h3>
                                    <div className="border rounded-2xl overflow-hidden bg-background p-6 space-y-6">
                                        <div className="grid md:grid-cols-2 gap-6">
                                            {/* Image Gallery */}
                                            <div className="space-y-3">
                                                <div className="aspect-square bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl flex items-center justify-center relative">
                                                    <div className="text-center">
                                                        <ImageIcon className="w-16 h-16 text-blue-300 mx-auto mb-2" />
                                                        <span className="text-sm text-blue-400">Main Image</span>
                                                    </div>
                                                    <div className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 text-[10px] px-2 py-0.5 rounded-full font-bold">← MAIN IMAGE</div>
                                                </div>
                                                <div className="flex gap-2">
                                                    {['Gallery 1', 'Gallery 2', 'Gallery 3'].map((g, i) => (
                                                        <div key={i} className="w-16 h-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center relative">
                                                            <span className="text-[9px] text-gray-400">{g}</span>
                                                            {i === 0 && <div className="absolute -bottom-4 left-0 bg-yellow-400 text-yellow-900 text-[9px] px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap">↑ GALLERY IMAGES</div>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Product Info */}
                                            <div className="space-y-4">
                                                <div>
                                                    <div className="text-xs text-muted-foreground uppercase mb-1">home • Tata Power Solar</div>
                                                    <h2 className="text-xl font-bold relative">
                                                        Tata Power Solar 3kW On-Grid System
                                                        <span className="text-[10px] font-normal text-yellow-600 ml-2">← Product Name</span>
                                                    </h2>
                                                </div>

                                                <div className="text-sm text-muted-foreground relative">
                                                    High-efficiency monocrystalline solar panel system with 25-year performance warranty.
                                                    <span className="text-[10px] font-bold text-yellow-600 ml-1">← Short Description</span>
                                                </div>

                                                <div className="flex items-center gap-4">
                                                    <span className="text-2xl font-bold text-primary">₹1,85,000</span>
                                                    <Badge className="bg-green-500/10 text-green-600 border-green-200">
                                                        <BadgePercent className="w-3 h-3 mr-1" /> After Subsidy: ₹1,07,000
                                                    </Badge>
                                                </div>

                                                {/* Features */}
                                                <div className="space-y-2 relative">
                                                    <span className="absolute -left-6 top-0 bg-yellow-400 text-yellow-900 text-[9px] px-1 py-0.5 rounded font-bold transform -rotate-90 origin-right">FEATURES →</span>
                                                    {['21.5% Module Efficiency', 'IP68 Dust & Water Resistant', 'Anti-PID Technology', '25-Year Performance Warranty'].map((f, i) => (
                                                        <div key={i} className="flex items-center gap-2 text-sm">
                                                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                            <span>{f}</span>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Info badges */}
                                                <div className="flex gap-4 pt-2">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <Shield className="w-4 h-4 text-blue-500" />
                                                        <span>25 Year Warranty <span className="text-[10px] text-yellow-600 font-bold">← Warranty Years</span></span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <Zap className="w-4 h-4 text-primary" />
                                                        <span>3 kW <span className="text-[10px] text-yellow-600 font-bold">← System Size</span></span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Tabs Preview */}
                                        <div className="border-t pt-4">
                                            <div className="flex gap-4 border-b">
                                                {['Description', 'Specifications', 'Reviews'].map((tab, i) => (
                                                    <button key={tab} className={`pb-2 text-sm font-medium border-b-2 ${i === 0 ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}>
                                                        {tab}
                                                        {i < 2 && <span className="text-[9px] text-yellow-600 font-bold ml-1">← YOUR DATA</span>}
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="pt-4 text-sm text-muted-foreground relative">
                                                <p>The Tata Power Solar 3kW system features premium monocrystalline cells with 21.5% efficiency. Designed for Indian rooftops, this system generates approximately 12-15 units of electricity daily...</p>
                                                <span className="text-[10px] text-yellow-600 font-bold block mt-1">↑ This is your "Long Description"</span>
                                            </div>
                                        </div>

                                        {/* Specs Table Preview */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <div className="bg-muted/50 px-4 py-2 text-sm font-semibold flex items-center justify-between">
                                                Specifications
                                                <span className="text-[10px] text-yellow-600 font-bold">← Specs Tab</span>
                                            </div>
                                            <div className="divide-y">
                                                {[
                                                    { label: 'Wattage', value: '400W' },
                                                    { label: 'Efficiency', value: '21.5%' },
                                                    { label: 'Dimensions', value: '1755×1038×35mm' },
                                                    { label: 'Weight', value: '21.5 kg' }
                                                ].map(spec => (
                                                    <div key={spec.label} className="flex items-center px-4 py-2 text-sm">
                                                        <span className="font-medium w-1/3">{spec.label}</span>
                                                        <span className="text-muted-foreground">{spec.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                {/* Field-by-Field Guide */}
                <div className="space-y-3">
                    <h2 className="text-lg font-semibold">Field-by-Field Guide</h2>

                    {sections.map((section, sectionIdx) => (
                        <motion.div
                            key={section.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.05 * sectionIdx }}
                            className="rounded-xl border bg-card overflow-hidden"
                        >
                            <button
                                className="w-full flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors text-left"
                                onClick={() => setExpandedSection(expandedSection === section.id ? '' : section.id)}
                            >
                                <div className={`w-10 h-10 ${section.color} rounded-xl flex items-center justify-center text-white flex-shrink-0`}>
                                    <section.icon className="w-5 h-5" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold">{section.title}</h3>
                                    <p className="text-xs text-muted-foreground">{section.description}</p>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${expandedSection === section.id ? 'rotate-180' : ''}`} />
                            </button>

                            <AnimatePresence>
                                {expandedSection === section.id && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="px-4 pb-4 space-y-4">
                                            {section.fields.map((field, fieldIdx) => (
                                                <div key={fieldIdx} className="rounded-lg border bg-muted/20 p-4 space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <h4 className="font-semibold flex items-center gap-2">
                                                            {field.name}
                                                            {field.required && (
                                                                <Badge variant="outline" className="text-red-500 border-red-200 text-[10px]">Required</Badge>
                                                            )}
                                                        </h4>
                                                        <Badge variant="outline" className="text-xs">
                                                            <MapPin className="w-3 h-3 mr-1" />
                                                            {field.mapsTo}
                                                        </Badge>
                                                    </div>

                                                    {/* Example */}
                                                    <div className="bg-background rounded-lg p-3 border">
                                                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 font-semibold">Example:</div>
                                                        {Array.isArray(field.example) ? (
                                                            typeof field.example[0] === 'string' ? (
                                                                <ul className="text-sm space-y-1">
                                                                    {field.example.map((item, i) => (
                                                                        <li key={i} className="flex items-center gap-2">
                                                                            <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
                                                                            {item}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            ) : (
                                                                <div className="grid grid-cols-2 gap-1 text-sm">
                                                                    {field.example.map((spec, i) => (
                                                                        <div key={i} className="flex gap-2">
                                                                            <span className="font-medium">{spec.label}:</span>
                                                                            <span className="text-muted-foreground">{spec.value}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )
                                                        ) : (
                                                            <p className="text-sm text-foreground">{field.example}</p>
                                                        )}
                                                    </div>

                                                    {/* Tip */}
                                                    <div className="flex items-start gap-2 text-sm">
                                                        <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                                                        <span className="text-muted-foreground">{field.tip}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </div>

                {/* Pro Tips */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="rounded-2xl border bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-950/20 dark:to-orange-950/20 p-6 space-y-4"
                >
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                        Pro Tips for Better Listings
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        {[
                            { title: 'Use Real Photos', desc: 'Avoid stock/generic images. Real product photos build trust and get 3x more clicks.', icon: ImageIcon },
                            { title: 'Be Specific', desc: 'Instead of "good panel", write "21.5% efficiency monocrystalline cell with anti-PID technology".', icon: FileText },
                            { title: 'Fill All Fields', desc: 'Complete listings rank higher in search. Don\'t skip specifications or features.', icon: CheckCircle2 },
                            { title: 'Accurate Pricing', desc: 'Set realistic market prices. Admin may adjust, but accurate suggestions get faster approvals.', icon: IndianRupee },
                            { title: 'Warranty Matters', desc: 'Customers filter by warranty. Always include the accurate warranty period.', icon: Shield },
                            { title: 'Subsidy Info', desc: 'Mark products as subsidy-eligible if they qualify. This attracts price-conscious buyers.', icon: BadgePercent }
                        ].map((tip, i) => (
                            <div key={i} className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-lg bg-background border flex items-center justify-center flex-shrink-0">
                                    <tip.icon className="w-4 h-4 text-primary" />
                                </div>
                                <div>
                                    <div className="font-semibold text-sm">{tip.title}</div>
                                    <div className="text-xs text-muted-foreground">{tip.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* CTA */}
                <div className="flex justify-center pb-8">
                    <Button onClick={() => navigate(userRole === 'admin' ? '/admin/products' : '/vendor/products')} size="lg" className="btn-primary text-lg px-8">
                        <Package className="w-5 h-5 mr-2" />
                        Start Adding Products
                        <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default VendorProductGuide;
